import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Stack,
  Chip
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import GpsNotFixedIcon from '@mui/icons-material/GpsNotFixed';
import GpsOffIcon from '@mui/icons-material/GpsOff';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { useGeolocation } from '../hooks/useGeolocation';
import { useProximidadeCliente } from '../hooks/useProximidadeCliente';
import { visitaService } from '../services/visita.service';
import type { ClienteMapa, Visita } from '../types/visita.types';

// AIDEV-NOTE: Modal de check-in com validacao GPS — exige proximidade <= 200m e accuracy <= 50m

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: (visita: Visita) => void;
  cliente: ClienteMapa;
}

export function CheckinModal({ open, onClose, onSuccess, cliente }: Props) {
  const { position, error: gpsError, loading: gpsLoading } = useGeolocation(true);
  const { distancia, estaProximo, podeCheckin } = useProximidadeCliente(
    position,
    cliente.lat,
    cliente.lng
  );
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleCheckin = async () => {
    if (!position || !podeCheckin) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      const visita = await visitaService.checkin({
        cliente_id: cliente.id,
        lat: position.lat,
        lng: position.lng,
        accuracy: position.accuracy
      });
      onSuccess(visita);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Erro ao realizar check-in');
    } finally {
      setSubmitting(false);
    }
  };

  // AIDEV-NOTE: Renderiza status do GPS com cores indicativas
  const renderGpsStatus = () => {
    if (gpsError) {
      return (
        <Chip
          icon={<GpsOffIcon />}
          label={gpsError.code === 1 ? 'Permissao negada' : 'GPS indisponivel'}
          color="error"
          size="small"
        />
      );
    }
    if (gpsLoading) {
      return <Chip icon={<CircularProgress size={14} />} label="Obtendo GPS..." size="small" />;
    }
    if (position && position.accuracy > 50) {
      return (
        <Chip
          icon={<GpsNotFixedIcon />}
          label={`GPS impreciso (~${Math.round(position.accuracy)}m)`}
          color="warning"
          size="small"
        />
      );
    }
    return <Chip icon={<GpsFixedIcon />} label="GPS pronto" color="success" size="small" />;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Check-in - {cliente.nome}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* Status GPS */}
          <Box>{renderGpsStatus()}</Box>

          {/* Distancia ao cliente */}
          {distancia !== null && (
            <Stack direction="row" spacing={1} alignItems="center">
              <LocationOnIcon sx={{ color: estaProximo ? 'success.main' : 'error.main' }} />
              <Typography
                variant="body2"
                color={estaProximo ? 'success.main' : 'error.main'}
                fontWeight="bold"
              >
                {distancia}m do cliente
              </Typography>
            </Stack>
          )}

          {/* Alerta de distancia */}
          {distancia !== null && !estaProximo && (
            <Alert severity="warning" icon={<WarningAmberIcon />}>
              Voce esta a {distancia}m do cliente. Aproxime-se para fazer check-in (maximo 200m).
            </Alert>
          )}

          {/* Alerta de precisao */}
          {position && position.accuracy > 50 && estaProximo && (
            <Alert severity="info">
              Aguarde o GPS estabilizar. Precisao atual: ~{Math.round(position.accuracy)}m
              (necessario &lt;= 50m).
            </Alert>
          )}

          {/* Alerta de cliente sem coordenadas */}
          {cliente.lat == null || cliente.lng == null ? (
            <Alert severity="error">
              Este cliente nao possui coordenadas cadastradas. Nao e possivel realizar check-in por
              GPS.
            </Alert>
          ) : null}

          {/* Erro de submit */}
          {submitError && <Alert severity="error">{submitError}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          onClick={handleCheckin}
          variant="contained"
          disabled={!podeCheckin || submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : <GpsFixedIcon />}
        >
          {submitting ? 'Registrando...' : 'Confirmar Check-in'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
